import { useEffect, useState } from 'react';

import { getWatchPlan, WatchPlan } from '../utils/api/getWatchPlan';

interface UseLoadWatchPlanResult {
  plan: WatchPlan | undefined;
  loading: boolean;
  error: boolean;
}

/**
 * Load a watch plan by Salesforce id.
 *
 * @param {string | undefined} watchPlanId - Resolved plan id, or undefined when slug is unknown.
 * @returns {UseLoadWatchPlanResult} Plan data plus loading / error flags.
 *
 * @example
 * const { plan, loading, error } = useLoadWatchPlan(watchPlanId);
 *
 * @see src/utils/api/getWatchPlan.ts
 */
const useLoadWatchPlan = (watchPlanId: string | undefined): UseLoadWatchPlanResult => {
  const [plan, setPlan] = useState<WatchPlan | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (watchPlanId === undefined) {
      setPlan(undefined);
      setLoading(false);
      setError(true);
      return undefined;
    }

    const load = async () => {
      setLoading(true);
      setError(false);
      setPlan(undefined);

      const result = await getWatchPlan(watchPlanId);

      if (cancelled) {
        return;
      }

      if (result === undefined) {
        setError(true);
        setPlan(undefined);
      } else {
        setPlan(result);
        setError(false);
      }

      setLoading(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [watchPlanId]);

  return { plan, loading, error };
};

export default useLoadWatchPlan;
